import * as THREE from 'three';
import { getTableLayout, getKnuckleOrder, ballRadius } from '../sim/world.js';
import {
  CUSHION_THICKNESS,
  CUSHION_END_CUT,
  CUSHION_NOSE_OFFSET,
} from './table-cushions.js';

/**
 * テーブル外周の木枠レール（自前・GPL 非依存）。
 *
 * 構築方法:
 *   - 外周 = テーブル全体の長方形
 *   - 内縁 = 各クッション（台形ジョー）の**外側カット角**を辿る 12 点 +
 *     各ポケットを丸く抉る半円アークで構成する多角形
 *
 * ポケット形状（2026-05-31 再修正）:
 *   - 半円の直径端点を、`table-cushions.ts` のクッション斜めカットの**外側角**
 *     （= ナックル ± CUSHION_END_CUT, 外側面）に一致させる。これによりクッション
 *     カットの外側と木枠半円が段差なく接続する。
 *   - 黒穴（`table-pockets.ts`）は「鼻先（少し内側に食い込む）→ カット外側角 →
 *     半円 → カット外側角 → 鼻先」のキーホール形状で開口のみを塗る。
 *
 * 座標系: 球中心 z=0 / クッション z∈[-R, R*0.5] / レール z∈[-R, R*0.65]
 */

const RAIL_TOP = ballRadius * 0.65;
const RAIL_BOTTOM = -ballRadius;
const RAIL_HEIGHT = RAIL_TOP - RAIL_BOTTOM;
const RAIL_GAP = ballRadius * 0.02;
const RAIL_WIDTH = ballRadius * 3.5; // 細すぎず太すぎず
// レール内縁（= クッション外側面 + 隙間）の seg.fixed からのオフセット。
// 鼻先(+R) → 厚み(+2R) → 隙間 の順に外側へ積む。
const RAIL_INNER_OFFSET = CUSHION_NOSE_OFFSET + CUSHION_THICKNESS + RAIL_GAP;

export const railTopZ = (): number => RAIL_TOP;
export const railBottomZ = (): number => RAIL_BOTTOM;
export const railCenterY = (halfY: number): number =>
  halfY + RAIL_INNER_OFFSET + RAIL_WIDTH / 2;
export const railCenterX = (halfX: number): number =>
  halfX + RAIL_INNER_OFFSET + RAIL_WIDTH / 2;
// レール外周（外側面）座標。キャビネットを footprint に合わせるのに使用。
export const railOuterX = (halfX: number): number =>
  halfX + RAIL_INNER_OFFSET + RAIL_WIDTH;
export const railOuterY = (halfY: number): number =>
  halfY + RAIL_INNER_OFFSET + RAIL_WIDTH;

const WOOD_MAT = new THREE.MeshStandardMaterial({
  color: 0x2a1812,
  roughness: 0.55,
  metalness: 0.04,
  envMapIntensity: 0.45,
});

/** 角度差の絶対値を [0, π] に正規化して返す。 */
const angleGap = (a: number, b: number): number => {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d <= -Math.PI) d += Math.PI * 2;
  return Math.abs(d);
};

/**
 * cutA, cutB を直径の両端とする半円（弦を直径とする真円弧）の内側点列を返す。
 * 半円は outwardRef（ポケット中心 = テーブル中心と反対側）へ膨らむ。両端は呼び出し側が
 * 別途 push するため、ここでは両端を除いた中間点のみ返す（cutA → cutB の順）。
 * エラー時は空配列（その場合は cutA-cutB が直線で結ばれる）。
 */
export const semicircleArc = (
  cutA: { x: number; y: number },
  cutB: { x: number; y: number },
  outwardRef: { x: number; y: number },
  segments = 24,
): { x: number; y: number }[] => {
  try {
    const mx = (cutA.x + cutB.x) / 2;
    const my = (cutA.y + cutB.y) / 2;
    const a = Math.hypot(cutB.x - cutA.x, cutB.y - cutA.y) / 2; // 半弦長 = 半円の半径
    if (!Number.isFinite(a) || a < 1e-9) return [];
    const angA = Math.atan2(cutA.y - my, cutA.x - mx);
    const outAng = Math.atan2(outwardRef.y - my, outwardRef.x - mx);
    // cutA から +π / -π のどちら回りで中央が外向きになるか
    const sweep =
      angleGap(angA + Math.PI / 2, outAng) < angleGap(angA - Math.PI / 2, outAng)
        ? Math.PI
        : -Math.PI;
    const pts: { x: number; y: number }[] = [];
    for (let i = 1; i < segments; i++) {
      const ang = angA + sweep * (i / segments);
      pts.push({ x: mx + Math.cos(ang) * a, y: my + Math.sin(ang) * a });
    }
    return pts;
  } catch (err) {
    console.error('semicircleArc failed:', err);
    return [];
  }
};

/** ナックル index → 隣接ポケット index（getTableLayout().pockets の並び）。 */
const KNUCKLE_POCKET: Record<number, number> = {
  0: 1, // SW
  1: 0, // NW
  2: 0, // NW
  3: 2, // N
  4: 2, // N
  5: 4, // NE
  6: 4, // NE
  7: 5, // SE
  8: 5, // SE
  9: 3, // S
  10: 3, // S
  11: 1, // SW
};

/** pocket index → 隣接ナックル index ペア（A→B の順で半円・開口を構成）。 */
const POCKET_KNUCKLE_PAIR: Record<number, [number, number]> = {
  0: [1, 2], // NW
  1: [11, 0], // SW
  2: [3, 4], // N
  3: [9, 10], // S
  4: [5, 6], // NE
  5: [7, 8], // SE
};

interface Pt {
  x: number;
  y: number;
}

/**
 * 各ナックルについて、クッション台形ジョーに整合する 2 点を算出する:
 *   - cut: 斜めカットの**外側角**（= ナックル ± CUSHION_END_CUT, 外側面）→ 木枠半円端点
 *   - mouth: 鼻先（内側面）を僅かに内側へ食い込ませた点 → 黒穴の口
 * 接線方向のシフトは隣接ポケット中心へ向ける。
 */
const knuckleGeometry = (): { cuts: Pt[]; mouths: Pt[]; pockets: Pt[] } => {
  const { halfX, halfY, pockets } = getTableLayout();
  const knuckles = getKnuckleOrder();
  const cuts: Pt[] = [];
  const mouths: Pt[] = [];
  knuckles.forEach((k, i) => {
    const p = pockets[KNUCKLE_POCKET[i] ?? 0]!;
    const isLongEdge = Math.abs(k.y) > halfY - ballRadius * 0.5;
    if (isLongEdge) {
      const signN = k.y > 0 ? 1 : -1;
      const shift = Math.sign(p.x - k.x) * CUSHION_END_CUT;
      cuts.push({ x: k.x + shift, y: signN * (halfY + RAIL_INNER_OFFSET) });
      // 口は鼻先（クッション内側面 = half + R）。食い込みは付けない。
      mouths.push({ x: k.x, y: signN * (halfY + CUSHION_NOSE_OFFSET) });
    } else {
      const signN = k.x > 0 ? 1 : -1;
      const shift = Math.sign(p.y - k.y) * CUSHION_END_CUT;
      cuts.push({ x: signN * (halfX + RAIL_INNER_OFFSET), y: k.y + shift });
      mouths.push({ x: signN * (halfX + CUSHION_NOSE_OFFSET), y: k.y });
    }
  });
  return { cuts, mouths, pockets: pockets.map((p) => ({ x: p.x, y: p.y })) };
};

/** コーナーポケットの index（getTableLayout().pockets 並び）。残り 2,3 はサイド。 */
const CORNER_POCKETS = new Set<number>([0, 1, 4, 5]);

/** ポケット開口の情報。getTableLayout().pockets と同じ並び。 */
export interface PocketOpening {
  mouthA: Pt; // 鼻先A（クッション内側面）
  cutA: Pt; // カット外側角A
  cutB: Pt; // カット外側角B
  mouthB: Pt; // 鼻先B
  center: Pt; // ポケット中心（外向き参照）
  isCorner: boolean; // コーナー = 黒の半円落とし穴を描く / サイド = 喉部のみ（半円なし）
}

/**
 * 各ポケットの開口形状を算出する（table-pockets が喉部緑 + 黒穴を構築するのに使用）。
 */
export const computePocketOpenings = (): PocketOpening[] => {
  try {
    const { cuts, mouths, pockets } = knuckleGeometry();
    return pockets.map((p, i) => {
      const pair = POCKET_KNUCKLE_PAIR[i];
      const isCorner = CORNER_POCKETS.has(i);
      if (!pair) {
        return { mouthA: p, cutA: p, cutB: p, mouthB: p, center: p, isCorner };
      }
      const [a, b] = pair;
      return {
        mouthA: mouths[a]!,
        cutA: cuts[a]!,
        cutB: cuts[b]!,
        mouthB: mouths[b]!,
        center: p,
        isCorner,
      };
    });
  } catch (err) {
    console.error('computePocketOpenings failed:', err);
    return [];
  }
};

export const addTableRails = (scene: THREE.Scene): void => {
  const { halfX, halfY, pockets } = getTableLayout();
  const outX = halfX + RAIL_INNER_OFFSET + RAIL_WIDTH;
  const outY = halfY + RAIL_INNER_OFFSET + RAIL_WIDTH;

  // ---- 外周（CCW） ----
  const shape = new THREE.Shape();
  shape.moveTo(-outX, -outY);
  shape.lineTo(outX, -outY);
  shape.lineTo(outX, outY);
  shape.lineTo(-outX, outY);
  shape.closePath();

  // ---- 内縁 = 12 カット外側角 + コーナーのみ半円アーク ----
  // サイド（N/S）は半円を入れず直線で結ぶ（木枠の丸い膨らみを廃止）。
  const { cuts } = knuckleGeometry();

  // 「直前ナックル index」→ そのコーナーポケット中心（外向き参照）。
  const arcAfter: Record<number, { x: number; y: number }> = {
    1: { x: pockets[0]!.x, y: pockets[0]!.y }, // NW
    5: { x: pockets[4]!.x, y: pockets[4]!.y }, // NE
    7: { x: pockets[5]!.x, y: pockets[5]!.y }, // SE
    11: { x: pockets[1]!.x, y: pockets[1]!.y }, // SW
  };

  const ccwPts: { x: number; y: number }[] = [];
  for (let i = 0; i < cuts.length; i++) {
    ccwPts.push(cuts[i]!);
    const pocketCenter = arcAfter[i];
    if (pocketCenter) {
      // 直前カット角 cuts[i] と次カット角を直径両端とする外向き半円で繋ぐ。
      const next = cuts[(i + 1) % cuts.length]!;
      const pts = semicircleArc(cuts[i]!, next, pocketCenter);
      ccwPts.push(...pts);
    }
  }
  const cwPts = [...ccwPts].reverse();

  const innerHole = new THREE.Path();
  cwPts.forEach((p, i) => {
    if (i === 0) innerHole.moveTo(p.x, p.y);
    else innerHole.lineTo(p.x, p.y);
  });
  innerHole.closePath();
  shape.holes.push(innerHole);

  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: RAIL_HEIGHT,
    bevelEnabled: false,
  });
  geo.translate(0, 0, RAIL_BOTTOM);

  const mesh = new THREE.Mesh(geo, WOOD_MAT);
  mesh.receiveShadow = true;
  mesh.castShadow = true;
  scene.add(mesh);
};
